<script lang="ts">
    import InstagramIcon from "@apps/instagram/components/icons/InstagramIcon.svelte";
    import CN from "classnames";
    import { Observable } from "rxjs";
    import { filter, switchMap, tap } from "rxjs/operators";
    import { onMount } from "svelte";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Icon from "@components/Icon.svelte";
    import Image from "@components/Image/Image.svelte";
    import Slider from "@components/Slider/Slider.svelte";
    import SliderController from "@components/Slider/SliderController";

    export let images: { id: number; url: string; altText: string | null }[];
    export let imageId$: Observable<number | null>;
    export let altText = "";

    const sliderController = new SliderController();
    const currentIndex$ = sliderController.connected$.pipe(
        filter((v) => !!v),
        switchMap(() => {
            return sliderController.selectCurrentSlideIndex();
        })
    );
    let slidesCount$ = sliderController.connected$.pipe(
        filter((v) => !!v),
        switchMap(() => {
            return sliderController.selectSlidesCount();
        })
    );
    const handleDots = (index: number) => {
        sliderController.goToIndex(index);
    };
    onMount(() => {
        const imageIndexSub = imageId$
            .pipe(
                filter((imageId): imageId is number => !!imageId),
                switchMap((imageId) => {
                    return sliderController.connected$.pipe(
                        filter((connected) => !!connected),
                        tap(() => {
                            const imageIndex = images.findIndex(
                                (image) => image.id === imageId
                            );
                            if (imageIndex) {
                                sliderController.goToIndex(imageIndex);
                            }
                        })
                    );
                })
            )
            .subscribe();
        return () => {
            imageIndexSub.unsubscribe();
        };
    });
</script>

<div class="gw-post-modal-product-image">
    <Slider
        perPage={1}
        {sliderController}
        slidesCount={$slidesCount$}
        loop={false}
    >
        <svelte:fragment slot="slides">
            {#each images as image}
                <div class="gw-post-modal-product-image__media">
                    <Image
                        alt={image.altText || altText}
                        wrapperClassName="gw-post-modal-product-image__image"
                        src={image.url}
                        objectFit="contain"
                    >
                        <Icon
                            slot="backdrop"
                            class="gw-post-modal-product-image__icon"
                        >
                            <InstagramIcon />
                        </Icon>
                    </Image>
                </div>
            {/each}
        </svelte:fragment>
    </Slider>
    <div class="gw-post-modal-product-image__dots">
        {#if images.length > 1}
            {#each images as image, idx (idx)}
                <BaseButton
                    class={CN("gw-post-modal-product-image__dot", {
                        "gw-post-modal-product-image__dot--active":
                            $currentIndex$ === idx,
                    })}
                    on:click={() => handleDots(idx)}
                >
                    <Image
                        ratio={1 / 1}
                        alt={image.altText || altText}
                        wrapperClassName="gw-post-modal-product-image__dots-image"
                        src={image.url}
                        objectFit="cover"
                    >
                        <Icon
                            slot="backdrop"
                            class="gw-post-modal-product-image__icon"
                        >
                            <InstagramIcon />
                        </Icon>
                    </Image>
                </BaseButton>
            {/each}
        {/if}
    </div>
</div>

<style>
    .gw-post-modal-product-image {
        display: flex;
        position: relative;
        align-items: center;
        width: 100%;
        height: 100%;
    }

    .gw-post-modal-product-image__media {
        display: flex;
        align-items: center;
        height: 100vh;
    }

    .gw-post-modal-product-image :global(.gw-post-modal-product-image__image) {
        display: block;
        width: 100%;
    }

    .gw-post-modal-product-image
        :global(.gw-image--loaded)
        :global(.gw-image__backdrop) {
        display: none;
    }
    .gw-post-modal-product-image :global(.gw-post-modal-product-image__icon) {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 100px;
        height: 100px;
        transform: translateX(-50%) translateY(-50%);
        color: #ccc;
    }
    .gw-post-modal-product-image__dots {
        display: flex;
        position: absolute;
        bottom: 20px;
        left: 20px;
        flex-wrap: wrap;
        justify-content: center;
        margin: 10px 0;
        column-gap: 4px;
        row-gap: 10px;
    }
    .gw-post-modal-product-image__dots
        :global(.gw-post-modal-product-image__dot) {
        width: 76px;
        height: 76px;
        padding: 3px;
        border-radius: 5px;
        background-color: rgba(255, 255, 255, 0.3);
    }
    .gw-post-modal-product-image__dots
        :global(.gw-post-modal-product-image__dot--active) {
        background-color: var(--gw-white-bg);
        cursor: default;
    }

    /* TODO: придумать более хороший способ задавать размер */
    .gw-post-modal-product-image :global(.gw-slider__slides > div),
    .gw-post-modal-product-image :global(.gw-slider__slides > div > div) {
        height: 100%;
    }

    @media all and (max-width: 1000px) {
        :global(.gw-post-modal-product-image__dot) {
            width: 60px;
            height: 60px;
        }
    }

    @media all and (max-width: 430px) {
        :global(.gw-post-modal-product-image__dot) {
            width: 45px;
            height: 45px;
        }
    }
</style>
